import {
  Army,
  ArmyList,
  ArmyListUnit,
  ArmyListUnitModelRuleTroop,
  ArmyOrganisation,
  Unit,
  UnitOptionCategory,
  UnitOptionUsePoint,
} from '@Models';
import { AppState } from '@Redux/store';
import { Dispatch, Store } from '@Redux/type';
import { OptionExport } from './view/export';

export const clearText = (txt: string): string => {
  return txt?.replace(/ \([a-zA-Z0-9+]+\)/i, '') ?? '';
};

export const getCurrentState =
  () =>
  (_: Dispatch, getState: Store): AppState => {
    return getState();
  };

export const getState = (state: AppState): AppState => state;

export const getArmy = (state: AppState): Army | null => state.army.entity;

export const getArmyList = (state: AppState): ArmyList | null =>
  state.armyList.entity;

export const getArmyOrganisation = (state: AppState): ArmyOrganisation | null =>
  getArmy(state)?.army_organisations?.find(
    (item) => item.id === getArmyList(state)?.army_organisation_id,
  ) ?? null;

export const getArmyListUnits = (state: AppState): ArmyListUnit[] =>
  state.armyList.entity?.army_list_units?.filter(
    (item) => item.is_enabled && !item.isDeleted,
  ) ?? [];

export const getCurrentArmyListUnit = (
  state: AppState,
  props: any,
): ArmyListUnit => props.armyListUnit;

export const showPoints = (state: AppState, props: any): boolean =>
  props.showPoints ?? true;

export const showDetails = (state: AppState, props: any): boolean =>
  props.showDetails ?? false;

export const getUnit = (state: AppState, props: any): Unit | null =>
  state.army.entity?.units?.find(
    (item) => item.id === props.armyListUnit.unit_id,
  ) ?? null;

export const getModelRuleTroops = (
  state: AppState,
  props: any,
): ArmyListUnitModelRuleTroop[] =>
  getCurrentArmyListUnit(state, props)?.modelRuleUnitTroops?.filter(
    (item) => item.isEnabled,
  ) ?? [];

export const getArmyListUnitOptions = (
  state: AppState,
  props: any,
): OptionExport[] => {
  const armyListUnitOptions =
    getCurrentArmyListUnit(state, props)?.army_list_unit_unit_options ?? [];
  const unitOptions =
    getUnit(state, props)?.optionGroups?.flatMap((o) => o.options) ?? [];

  const details = showDetails(state, props);

  const options = armyListUnitOptions
    .filter((item) => !item.isDeleted)
    .flatMap((item) => {
      const option = unitOptions.find((o) => o.id === item.unit_option_id)!;

      let name = clearText(item.name);

      if (item.quantity > 1 && (!option || !option.is_per_model)) {
        name = `${item.quantity}x ${name}`;
      }

      if (details) {
        name += ` (${item.points})`;
      }

      const results: OptionExport[] = [
        {
          unitOptionId: item.unit_option_id,
          category: option?.category ?? UnitOptionCategory.Generic,
          name: name,
          points: item.points,
          parentId: option?.parent_id ?? null,
        } satisfies OptionExport as OptionExport,
      ];

      if (
        option?.parent_id &&
        option?.category === UnitOptionCategory.MagicItem
      ) {
        const parent = unitOptions.find(
          (o) =>
            o.id === option.parent_id &&
            (o.category === UnitOptionCategory.UnitEquipment ||
              (o.category === UnitOptionCategory.Equipment &&
                o.use_points === UnitOptionUsePoint.NoCost)),
        );
        if (parent)
          results.push({
            unitOptionId: parent.id,
            category: parent.category,
            name: details
              ? `${parent.name} (${parent.points ?? 0})`
              : parent.name,
            points: 0,
            parentId: null,
          } satisfies OptionExport as OptionExport);
      }

      return results.reverse();
    })
    .filter(
      (item, index, self) =>
        self.findIndex((i) => i.unitOptionId === item.unitOptionId) === index,
    );

  const filteredOptions: number[] = [];
  return options
    .map((item) => {
      if (
        ![
          UnitOptionCategory.BSB,
          UnitOptionCategory.MagicBanner,
          UnitOptionCategory.UnitEquipment,
          UnitOptionCategory.Equipment,
        ].includes(item.category)
      )
        return item;

      const banners = options.filter((o) => o.parentId === item.unitOptionId);
      if (banners.length === 0) return item;

      filteredOptions.push(...banners.map((o) => o.unitOptionId));

      item.name = `${item.name} (${banners.map((b) => b.name).join(', ')})`;
      item.points += banners.map((b) => b.points).reduce((a, b) => a + b, 0);

      return item;
    })
    .filter((item) => !filteredOptions.includes(item.unitOptionId));
};
